import { forwardRef, useImperativeHandle, useState } from 'react';
import { Form } from 'react-bootstrap';
import { ActionMeta } from 'react-select';
import { useBranchContext } from 'providers/BranchProvider';
import axiosInstance from 'utils/axiosInterceptors';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import ReactSelect from 'components/base/ReactSelect';
import { AddResupplyResult } from '../PurchaseOrder/CreateQuickPurchaseOrderForm';
import { SelectOption } from '../PurchaseOrder/CreatePurchaseOrderForm';
import { useProductTypeahead } from '../PurchaseOrder/useProductTypeahead';

export type AddResupplyItemFormHandle = {
  submit: () => Promise<AddResupplyResult | false>;
};

const AddResupplyItemForm = forwardRef<AddResupplyItemFormHandle>(
  (_, ref) => {
    const { selectedBranch } = useBranchContext();
    const { options, isLoading, handleSearchChange, noOptionsMessage } =
      useProductTypeahead();
    const [selectedProduct, setSelectedProduct] = useState<SelectOption | null>(
      null
    );
    const [code, setCode] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [comment, setComment] = useState('');

    const handleProductChange = (
      newValue: unknown,
      actionMeta: ActionMeta<unknown>
    ) => {
      if (actionMeta.action === 'select-option') {
        const option = newValue as SelectOption;
        setSelectedProduct(option);
        setCode('');
        return;
      }
      if (actionMeta.action === 'clear') {
        setSelectedProduct(null);
      }
    };

    const submit = async (): Promise<AddResupplyResult | false> => {
      if (!selectedBranch?.branchDomain) {
        toast.error('Branch not selected', { ...toastConfig, autoClose: 3000 });
        return false;
      }
      if (!selectedProduct && !code.trim()) {
        toast.error('Select a product or enter a product code', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }
      if (!quantity || quantity < 1) {
        toast.error('Quantity must be at least 1', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }

      try {
        const { data } = await axiosInstance.post<AddResupplyResult>(
          `/api/v1/${selectedBranch.branchDomain}/resupply`,
          selectedProduct
            ? {
                productId: selectedProduct.value,
                quantity,
                unitOfMeasure: 'Piece',
                comment: comment.trim() || null
              }
            : {
                code: code.trim(),
                quantity,
                unitOfMeasure: 'Piece',
                comment: comment.trim() || null
              }
        );

        toast.success(
          data.quantityBumped
            ? 'Quantity added on the resupply list.'
            : data.isUnencoded
              ? 'Added to resupply. Potential sources will show if similar catalog items exist.'
              : 'Added to resupply.',
          { ...toastConfig, autoClose: 3000 }
        );
        return data;
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(
            error.response?.data?.errorMessage ||
              error.response?.data?.message ||
              'Unable to add to resupply',
            { ...toastConfig, autoClose: 3000 }
          );
          return false;
        }
        toast.error('Unable to add to resupply', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }
    };

    useImperativeHandle(ref, () => ({ submit }));

    return (
      <Form>
        <Form.Group className="mb-3">
          <Form.Label>Product</Form.Label>
          <ReactSelect
            value={selectedProduct}
            placeholder="Search catalog (type at least 2 characters)"
            options={options}
            onChange={handleProductChange}
            onInputChange={handleSearchChange}
            filterOption={null}
            noOptionsMessage={noOptionsMessage}
            isLoading={isLoading}
            closeMenuOnSelect
            isSearchable
            isClearable
          />
          <Form.Text className="text-body-tertiary">
            Pick a catalog product, or leave this blank and enter a code below
            if it is not in the catalog yet.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Code / name</Form.Label>
          <Form.Control
            type="text"
            value={code}
            disabled={selectedProduct != null}
            placeholder="OEM / barcode / part code"
            onChange={e => setCode(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Quantity</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={quantity}
            onChange={e => setQuantity(Number(e.target.value))}
          />
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label>Comment (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={500}
            value={comment}
            placeholder="e.g. for Toyota Vios"
            onChange={e => setComment(e.target.value)}
          />
          <Form.Text className="text-body-tertiary">
            Extra info such as which vehicle this is for.
          </Form.Text>
        </Form.Group>
      </Form>
    );
  }
);

AddResupplyItemForm.displayName = 'AddResupplyItemForm';

export default AddResupplyItemForm;
